import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, Mail, Send, MessageCircle, Instagram, 
  HelpCircle, Building2, HeartHandshake, MessageSquare
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    category: "general"
  });

  const categories = [
    { value: "general", label: "General Inquiry", icon: HelpCircle },
    { value: "partnership", label: "Partnership / Provider", icon: Building2 },
    { value: "support", label: "Support", icon: HeartHandshake },
    { value: "feedback", label: "Feedback", icon: MessageSquare }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 hero-gradient rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Message Sent!
          </h1>
          <p className="text-slate-600 mb-8">
            Thank you for reaching out. Our team will get back to you within 24-48 hours at {formData.email}
          </p>
          <div className="flex flex-col gap-3">
            <Link to="/">
              <Button className="w-full hero-gradient text-white rounded-full">
                Back to Home
              </Button>
            </Link>
            <Button 
              variant="outline" 
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: "", email: "", subject: "", message: "", category: "general" });
              }}
              className="w-full rounded-full"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <div className="hero-gradient text-white p-6 pt-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Contact Us
            </h1>
          </div>
          <p className="text-white/80">
            We'd love to hear from you. Get in touch with our team.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-semibold text-slate-800 mb-4">Get in Touch</h3>
              
              <div className="space-y-4">
                <a href="mailto:team@kiddiplayclub.com" className="flex items-center gap-3 text-slate-600 hover:text-purple-600 transition-colors">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Email</p>
                    <p className="text-sm">team@kiddiplayclub.com</p>
                  </div>
                </a>

                <a href="https://instagram.com/kiddiplay_uk" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-600 hover:text-pink-600 transition-colors">
                  <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                    <Instagram className="w-5 h-5 text-pink-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Instagram</p>
                    <p className="text-sm">@kiddiplay_uk</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100">
              <MessageCircle className="w-8 h-8 text-purple-500 mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2">Provider Partnership</h3>
              <p className="text-sm text-slate-600">
                Interested in listing your family-friendly business on KiddiPlay? Get in touch to discuss partnership opportunities.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-6">
              <div>
                <Label className="text-slate-700 mb-2 block">What can we help you with?</Label>
                <div className="grid grid-cols-2 gap-3">
                  {categories.map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        formData.category === cat.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 hover:border-purple-200'
                      }`}
                    >
                      <cat.icon className={`w-5 h-5 mb-1 ${formData.category === cat.value ? 'text-purple-500' : 'text-slate-400'}`} />
                      <p className={`text-sm font-medium ${formData.category === cat.value ? 'text-purple-700' : 'text-slate-600'}`}>
                        {cat.label}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Jane Smith"
                    className="mt-1"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="jane@example.com"
                    className="mt-1"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="How can we help?"
                  className="mt-1"
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <textarea
                  id="message"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Tell us more..."
                  rows={5}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full hero-gradient text-white rounded-full py-6 text-lg"
                data-testid="contact-submit-btn"
              >
                {loading ? "Sending..." : "Send Message"}
                <Send className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
